import { useCallback, useState, type FormEvent } from 'react';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { DrawingDocument } from '../generated/drawing-contract';
import { importSmiles } from '@peruselab/structure-editor/chemistry/smilesImport';

export type SmilesImportState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading' }
  | { readonly state: 'success'; readonly canonicalSmiles: string }
  | { readonly state: 'error'; readonly message: string };

export function useSmilesImport(
  paperSize: PaperSize,
  commitImportedDocument: (next: DrawingDocument, importPaperSize: PaperSize) => void,
  onImported: () => void
) {
  const [smilesInput, setSmilesInput] = useState('');
  const [smilesImportState, setSmilesImportState] = useState<SmilesImportState>({ state: 'idle' });

  const runSmilesImport = useCallback(
    // Resolves to how the import ended, for a caller that reports it in its own panel (#1059).
    async (smiles: string): Promise<SmilesImportState> => {
      setSmilesImportState({ state: 'loading' });
      let outcome: SmilesImportState;
      try {
        const result = await importSmiles(smiles, paperSize);
        commitImportedDocument(result.document, paperSize);
        onImported();
        outcome = { state: 'success', canonicalSmiles: result.canonicalSmiles };
      } catch (error) {
        outcome = {
          state: 'error',
          message: error instanceof Error ? error.message : 'Unable to import this SMILES string.'
        };
      }
      setSmilesImportState(outcome);
      return outcome;
    },
    [commitImportedDocument, onImported, paperSize]
  );

  const handleSmilesImport = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      return runSmilesImport(smilesInput);
    },
    [runSmilesImport, smilesInput]
  );

  const updateSmilesInput = useCallback((value: string) => {
    setSmilesInput(value);
    setSmilesImportState({ state: 'idle' });
  }, []);

  // #485: lets a caller that shows smilesImportState somewhere other than SmilesImportPanel (the
  // top-bar search dropdown's PubChem-import status, see TopBar.tsx) dismiss a shown error/success
  // without touching smilesInput -- unlike updateSmilesInput, which would clobber whatever's typed
  // into the File drawer's own paste-a-SMILES field if that happened to be open at the same time.
  const resetSmilesImportState = useCallback(() => {
    // Guards reference equality so calling this on every keystroke (WorkspaceShell's
    // onQueryChange) doesn't force a re-render once it's already idle.
    setSmilesImportState((current) => (current.state === 'idle' ? current : { state: 'idle' }));
  }, []);

  // Programmatic counterpart to handleSmilesImport, for callers that already have a SMILES
  // string in hand (e.g. a PubChem lookup result) rather than a form submission -- shares the
  // same import/commit/state pipeline, per #135's decision to feed external lookups through the
  // existing SMILES-import path rather than a new one.
  const importSmilesString = useCallback(
    (smiles: string) => {
      setSmilesInput(smiles);
      return runSmilesImport(smiles);
    },
    [runSmilesImport]
  );

  return {
    smilesInput,
    updateSmilesInput,
    smilesImportState,
    resetSmilesImportState,
    handleSmilesImport,
    importSmilesString
  };
}
